import { IMAssessmentProblem, IMStandardsGroup } from '../types'
import {
  createUnorderedList,
  PARAGRAPH_SPACING,
  wrapB,
  wrapP,
} from '../../html_utility'
import { nanoid } from 'nanoid'
import {
  IClassifyingItem,
  IClassifyingTarget,
} from '../../../components/ingestion/IngestionQuestions'
import { createIMSkillsList } from './standards'
import { parseHtmlString } from 'ingestion/nodeCreationHelpers'
import {
  IInlineNode,
  INoteNode,
  IQuestionNode,
  ISimplifiedNode,
  ISkillsList,
} from '../../../components/ingestion/IngestedTree'
import { NodeType } from 'shared/types/ingestion'
import { createMatchingQuestion } from './createNodeWrappers'
import { alphabetIndex } from '../../../utility'
import { IM360Parser } from './IM360Parser'
import { conditionalParagraphWithHeader } from '../../nodeCreationHelpers'
import { respondingToStudentThinkingNodes } from './respondingToStudentThinking'
import { IMReference } from '../types/reference'
import { getMultiParts } from './utility'

export function parseQuestions({
  assessment_problems,
}: {
  assessment_problems?: IMAssessmentProblem[]
}) {
  if (!assessment_problems) return []
  return assessment_problems.flatMap(parseQuestion)
}

function convertQuestion({
  problem_response,
  statement: description,
}: IMAssessmentProblem): IQuestionNode | IInlineNode {
  const { response_type } = problem_response
  switch (response_type) {
    case 'RestrictedConstructedResponse':
    case 'ExtendedConstructedResponse':
    case 'ShortAnswerResponse':
      return parseShortAnswerOrMultiPart({
        description,
        ...problem_response,
      })
    case 'MultipleSelectResponse':
      return parseMultiSelectResponse({ description, ...problem_response })
    case 'MultipleChoiceResponse':
      return parseMultiChoiceResponse({ description, ...problem_response })
    case 'MatchingResponse':
      return parseMatchingResponse({ description, ...problem_response })
    default:
      throw new Error(`Unrecognized question type ${response_type}`)
  }
}

function questionPreNodes(questionData: IMAssessmentProblem) {
  const { ancillary_content, ordinal_title } = questionData
  const nodes: ISimplifiedNode[] = []
  if (ancillary_content.goals?.length) {
    nodes.push(
      IM360Parser.getInstance().createNoteNode({
        title: `Goals Assessed For ${ordinal_title.replace('Problem', 'Question')}`,
        value: createUnorderedList(
          ancillary_content.goals.map(({ title }) => title),
        ),
        studentFacing: false,
      }),
    )
  }
  nodes.push(...questionNarrative(questionData))
  return nodes
}

export function getReferenceString(references?: IMReference[]) {
  if (!references?.length) return ''
  const [reference] = references
  return wrapP(
    `The content assessed in this problem is first encountered in ${reference.ordinal_title}: ${reference.title}`,
  )
}

function questionNarrative({
  narrative,
  references,
  ordinal_title,
}: IMAssessmentProblem) {
  if (!narrative) return []
  const ref = getReferenceString(references)
  return [
    IM360Parser.getInstance().createNoteNode({
      title: `Narrative For ${ordinal_title.replace('Problem', 'Question')}`,
      value: ref + narrative,
      studentFacing: false,
    }),
  ]
}

function questionPostNodes(questionData: IMAssessmentProblem) {
  return respondingToStudentThinkingNodes(
    questionData.responding_to_student_thinking,
  )
}

export function questionStandardsNode(
  standards: IMStandardsGroup,
): [ISkillsList] | [] {
  const standardsList = [
    ...(standards.addressing ?? standards.building_toward ?? []),
  ]
  if (!standardsList.length) return []
  const skillsList = createIMSkillsList('Standards', standardsList)
  return [skillsList]
}

export function parseQuestion(questionData: IMAssessmentProblem) {
  const question = convertQuestion(questionData)
  if (questionData.ancillary_content.external_infos?.length) {
    question.data.title = questionData.ancillary_content.external_infos[0].title
  }
  const skillsList = questionStandardsNode(questionData.standards)
  if (question.type === NodeType.Question) {
    question.nodes = skillsList
  } else {
    question.nodes.forEach((n) => {
      if (n.type === NodeType.Question) {
        n.nodes = skillsList
      }
    })
  }
  return [
    ...questionPreNodes(questionData),
    question,
    ...questionPostNodes(questionData),
  ]
}

type ShortAnswerProps = {
  description?: string
  solution?: string
  teacherNote?: string
}

function parseShortAnswerOrMultiPart(props: ShortAnswerProps) {
  return (
    parseMultiPart(props) ??
    hiddenMultiPartQuestion(props) ??
    parseShortAnswer(props)
  )
}

function parseMultiPart({ description, solution }: ShortAnswerProps) {
  const { parts: questions, metaContent: questionStem } =
    getMultiParts(description!) ?? {}
  const { parts: answers, metaContent: answerContent } =
    getMultiParts(solution ?? '') ?? {}

  if (!questions?.length || questions.length !== answers?.length) {
    return
  }
  const parser = IM360Parser.getInstance()
  const questionNodes: (IQuestionNode | INoteNode)[] = questions.map(
    (q, index) =>
      parser.createShortAnswerOrResponse({
        description: q,
        teacherNote: answers[index],
      }),
  )

  const hasAnswerContent = !!answerContent
    ?.replace('Sample responses:', '')
    .replace('Sample response:', '')
    .trim()
  if (hasAnswerContent) {
    const last = [...questionNodes].pop()! as IQuestionNode
    last.data.teacherNote += `${PARAGRAPH_SPACING}${wrapB('Teacher Note')}${PARAGRAPH_SPACING}${answerContent}`
  }

  return IM360Parser.getInstance().createMultiPartQuestion(
    questionStem ?? '',
    questionNodes,
  )
}

const hiddenMultiPartRegex = /<span>([a-z]\.)<\/span>/g
function hiddenMultiPartQuestion({
  description = '',
  solution = '',
  teacherNote = '',
}: ShortAnswerProps) {
  const matches = description.match(hiddenMultiPartRegex)

  if (!matches || matches.length <= 1) return

  const asHtml = parseHtmlString(description)
  const figures = Array.from(asHtml.getElementsByTagName('figure'))
  if (figures.length !== matches.length) {
    throw new Error('Bad hiddenMultiPartQuestion assumption')
  }
  const answers = Array.from(
    parseHtmlString(solution).getElementsByTagName('li'),
  ).map((li) => li.innerHTML)
  const questions = figures.map((fig, index) => {
    fig.remove()
    Array.from(fig.getElementsByTagName('figcaption')).forEach((c) =>
      c.remove(),
    )
    return IM360Parser.getInstance().createShortAnswer({
      description: fig.outerHTML,
      teacherNote: answers[index],
    })
  })
  if (teacherNote) {
    throw new Error(`Bad Assumption: ${teacherNote}`)
  }
  return IM360Parser.getInstance().createMultiPartQuestion(
    asHtml.body.innerHTML,
    questions,
  )
}

const hiddenAnswerOptionRegex = /<span>([A-Z])<\/span>/g

function hiddenMultiChoiceParts({
  description = '',
  solution = '',
  teacherNote = '',
}: ShortAnswerProps) {
  const solutionMatch = hiddenAnswerOptionRegex.exec(solution)?.[1]
  if (!solutionMatch) return
  const asHtml = parseHtmlString(description)
  const grid = asHtml.getElementsByClassName('imgrid').item(0)
  if (!grid || !grid.innerHTML.match(hiddenAnswerOptionRegex)) return
  grid.remove()
  const options = Array.from(grid.getElementsByClassName('g--column'))
  if (!options.length) return
  const correctIndex = alphabetIndex(solutionMatch)

  const answers = options.map((description, index) => ({
    description: description.innerHTML.replaceAll(hiddenAnswerOptionRegex, ''),
    correct: index === correctIndex,
  }))

  return IM360Parser.getInstance().createMultiAnswerQuestion({
    description: asHtml.body.innerHTML,
    answers,
    teacherNote,
  })
}

function parseShortAnswer(data: ShortAnswerProps) {
  const multi = hiddenMultiChoiceParts(data)
  if (multi) return multi
  const { description = '', solution, teacherNote } = data
  const solutionPart = conditionalParagraphWithHeader('Solution:', solution)
  const teacherPart = teacherNote ? wrapP(teacherNote) : ''
  const combined = `${solutionPart}${teacherPart}`
  return IM360Parser.getInstance().createShortAnswerOrResponse({
    description,
    teacherNote: combined,
  })
}

function mapCharacterToIndex(char: string) {
  return char.charCodeAt(0) - 65
}

function parseMultiSelectResponse({
  description = '',
  choices,
  solution,
  teacherNote = '',
}: {
  description?: string
  choices?: string[]
  solution: string[]
  teacherNote?: string
}) {
  if (!choices) {
    return IM360Parser.getInstance().createShortAnswerOrResponse({
      description,
      teacherNote: teacherNote + wrapP(solution.join(', ')),
    })
  }
  const indexes = solution.map(mapCharacterToIndex)
  const answers = choices
    ?.filter((c) => !!c)
    .map((description, index) => ({
      description,
      correct: indexes.includes(index),
    }))
  return IM360Parser.getInstance().createMultiAnswerQuestion({
    description,
    answers,
    teacherNote,
  })
}

function parseMultiChoiceResponse({
  description = '',
  choices,
  solution,
  teacherNote,
}: {
  description?: string
  choices: string[]
  solution: number
  teacherNote?: string
}) {
  const answers = choices
    .filter((c) => !!c)
    .map((description, index) => ({ description, correct: index === solution }))
  return IM360Parser.getInstance().createMultiAnswerQuestion({
    description,
    answers,
    teacherNote,
  })
}

function parseMatchingResponse({
  description = '',
  choices,
  prompts,
  solution,
  teacherNote,
}: {
  description?: string
  choices: string[]
  prompts: string[]
  solution: string[]
  teacherNote?: string
}) {
  const targets = prompts.reduce<IClassifyingTarget[]>((acc, description) => {
    if (!description) return acc
    return [
      ...acc,
      {
        description,
        ExternalID: nanoid(10),
      },
    ]
  }, [])
  const items = choices.reduce<IClassifyingItem[]>((acc, description) => {
    if (!description) return acc
    return [
      ...acc,
      {
        description,
        targets: [],
      },
    ]
  }, [])
  solution.forEach((itemKey, index) => {
    if (!itemKey || itemKey.toLowerCase() === 'no match') return
    const itemIndex = parseInt(itemKey) - 1
    const item = items[itemIndex]
    const target = targets[index]
    item.targets.push(target.ExternalID)
  })
  return createMatchingQuestion({
    description,
    targets,
    items,
    teacherNote,
  })
}
